// src/KnotsGalleryAdaptive.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

/* layout constants */
const TILE_GAP = 16;
const MOBILE_BREAKPOINT = 640;

/* external links */
const MAGIC_EDEN_URL = "https://magiceden.io/ordinals/marketplace/bitcoinknots";

/* Thumbnail size presets */
/* small = xs; large = S; xl = L (special handling) */
const SIZE_PRESETS = {
  small: { min: 130, target: 158, max: 188 }, // xs
  large: { min: 198, target: 242, max: 286 }, // S
  xl: { min: 260, target: 320, max: 520 }, // L
};

/* UI paging */
const GALLERY_PAGE_SIZE = 50;

/* ✅ fixed totals for front-end UI (hide loading churn) */
const FIXED_TOTAL_ITEMS = 1000;
const FIXED_TOTAL_PAGES = Math.max(1, Math.ceil(FIXED_TOTAL_ITEMS / GALLERY_PAGE_SIZE));

/* history scan paging (API) */
const HISTORY_SCAN_LIMIT = 250; // per cursor request
const MAX_INSCRIBED = 1500; // safety cap (used if not fixed totals)

/* launch mode (kept) */
const LAUNCH_GALLERY_MAX = 50;

/* placeholders */
const PENDING_ID = "xxxx...xxi0";

/* ───── helpers ───── */
const fetchJsonAllowError = async (url, opts = {}) => {
  const r = await fetch(url, opts);
  const t = await r.text();
  let j;
  try {
    j = JSON.parse(t || "{}");
  } catch {
    j = {};
  }
  return { ok: r.ok, status: r.status, data: j, text: t };
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

const cleanTxid = (tx) => (tx ? String(tx).replace(/i0$/i, "") : "");

const parseKnotFromText = (txt) => {
  if (!txt) return NaN;
  const m = String(txt).match(/(?:^|\b)knot\s*(\d+)/i);
  if (m) return num(m[1]);
  const any = String(txt).match(/(\d{1,6})/);
  return any ? num(any[1]) : NaN;
};

const getKnotNoNumeric = (it) => {
  const direct = num(it?.knot_no ?? it?.knotNo ?? it?.knot_number ?? it?.knotNumber);
  if (Number.isFinite(direct)) return direct;
  const byName = parseKnotFromText(it?.knot_name ?? it?.name);
  if (Number.isFinite(byName)) return byName;
  const byAsset = parseKnotFromText(it?.asset_id);
  if (Number.isFinite(byAsset)) return byAsset;
  return NaN;
};

const tsCompleted = (it) => num(it?.completed_ts ?? it?.completedTs ?? 0) || 0;
const tsReserved = (it) => num(it?.reserved_ts ?? it?.reservedTs ?? 0) || 0;

const sortByKnotDescThenTime = (a, b) => {
  const ak = getKnotNoNumeric(a);
  const bk = getKnotNoNumeric(b);
  const aUn = Number.isNaN(ak);
  const bUn = Number.isNaN(bk);
  if (aUn && !bUn) return 1;
  if (!aUn && bUn) return -1;
  if (!aUn && !bUn && ak !== bk) return bk - ak;

  const at = tsCompleted(a) || tsReserved(a) || 0;
  const bt = tsCompleted(b) || tsReserved(b) || 0;
  if (at !== bt) return at - bt;

  return String(a?.asset_id || "").localeCompare(String(b?.asset_id || ""));
};

const displayKnotName = (it) => {
  if (it?.knot_name) return it.knot_name;
  const n = getKnotNoNumeric(it);
  if (Number.isFinite(n)) return `Knot ${n}`;
  const base = (it?.asset_id || "").split("/").pop();
  return base || "Knot";
};

const ordLink = (tx) => `https://ordinals.com/inscription/${tx}i0`;

const formatInscriptionId = (txBase) => {
  const s = txBase || "";
  if (!s) return PENDING_ID;
  const head = s.slice(0, 4);
  const tail = s.slice(-4);
  return `${head}...${tail}i0`;
};

/* deterministic RNG + shuffle (for random sort) */
function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleInPlace(arr, seed) {
  const rnd = mulberry32(seed);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

/* gallery sorting helper — supports asc/desc/random (normal mode) */
function sortGalleryItems(items, previewMode, sortMode, randomSeed) {
  const arr = [...items];

  if (previewMode) {
    const getIdx = (it) => Number(it.preview_index ?? 0) || 0;
    arr.sort((a, b) => getIdx(a) - getIdx(b));
    if (sortMode === "desc") arr.reverse();
    if (sortMode === "random") shuffleInPlace(arr, randomSeed);
    return arr;
  }

  if (sortMode === "asc") {
    arr.sort((a, b) => sortByKnotDescThenTime(b, a));
    return arr;
  }

  if (sortMode === "random") {
    shuffleInPlace(arr, randomSeed);
    return arr;
  }

  arr.sort(sortByKnotDescThenTime);
  return arr;
}

/* ✅ Download original image bytes (normal size, no magnification, no new tab) */
async function downloadPngOriginal(url, knotNumber = null) {
  const safeName = Number.isFinite(knotNumber) ? `Knot_${knotNumber}.png` : "Knot.png";

  const downloadBlob = (blob) => {
    const dlUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = dlUrl;
    a.download = safeName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(dlUrl), 250);
  };

  try {
    const r = await fetch(url, { mode: "cors", credentials: "omit", cache: "no-store" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    if (!blob || !blob.size) throw new Error("Empty download blob");
    downloadBlob(blob);
  } catch (e) {
    console.warn("downloadPngOriginal failed:", e);
    alert("Download failed (requires CORS). The image endpoint must allow CORS so the app can fetch the file.");
  }
}

/* simple mono icon */
function MonoIcon({ src, size = 12, color = "#949494", opacity = 1 }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: size,
        height: size,
        backgroundColor: color,
        WebkitMaskImage: `url(${src})`,
        maskImage: `url(${src})`,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        opacity,
      }}
    />
  );
}

/* ✅ Lazy image w/ retries */
function LazyImage({ src, alt, tileBoxStyle, placeholderStyle, pixelate = false, onClick }) {
  const holderRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [failed, setFailed] = useState(false);

  // retry
  const [attempt, setAttempt] = useState(0);
  const MAX_RETRIES = 3;

  useEffect(() => {
    if (!holderRef.current) return;
    let obs;
    try {
      obs = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      });
      obs.observe(holderRef.current);
    } catch {
      const t = setTimeout(() => setInView(true), 200);
      return () => clearTimeout(t);
    }
    return () => obs && obs.disconnect();
  }, []);

  // cache-bust only on retry
  const finalSrc = attempt === 0 ? src : `${src}${src.includes("?") ? "&" : "?"}r=${attempt}`;

  const handleError = () => {
    if (attempt < MAX_RETRIES) {
      const next = attempt + 1;
      setTimeout(() => setAttempt(next), 250 * next);
      return;
    }
    setFailed(true);
  };

  return (
    <div ref={holderRef} style={tileBoxStyle}>
      {!inView || failed ? (
        <div style={placeholderStyle} />
      ) : (
        <img
          key={finalSrc}
          src={finalSrc}
          alt={alt || ""}
          loading="lazy"
          onError={handleError}
          onClick={onClick}
          draggable={false}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            imageRendering: pixelate ? "pixelated" : "auto",
            cursor: onClick ? "pointer" : "default",
            userSelect: "none",
          }}
        />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/* Gallery component: inscribed-only, page-1 first, background scan */
/* ────────────────────────────────────────────────────────────── */
export default function KnotsGalleryAdaptive({
  styles,
  showAllAssets, // ignored in inscribed-only mode
  theme,
  sizeMode,
  smoothingMode = "auto",
  previewMode = false,
  onThemePresetChange, // kept (UI removed)
  ORCH_API,
  DL_ICON_SRC,
  launchGalleryMode = false,
  onPageChange,
}) {
  void showAllAssets;
  void onThemePresetChange;
  void theme;

  // ✅ "fixed totals" only in normal mode
  const usingFixedTotals = !previewMode && !launchGalleryMode;
  const scanCap = usingFixedTotals ? FIXED_TOTAL_ITEMS : MAX_INSCRIBED;

  const [previewItems, setPreviewItems] = useState([]);

  const [pageMap, setPageMap] = useState(() => new Map());
  const pageMapRef = useRef(pageMap);

  // keep ref synced before other effects
  useLayoutEffect(() => {
    pageMapRef.current = pageMap;
  }, [pageMap]);

  const [loadedCount, setLoadedCount] = useState(0);
  const scanLockRef = useRef(false);
  const [scanCursor, setScanCursor] = useState(null);
  const [scanHasMore, setScanHasMore] = useState(false);
  const [scanRunning, setScanRunning] = useState(false);

  // ───── scan debug (console-only) ─────
  const DEBUG_SCAN = false;
  const scanEventsRef = useRef([]);
  const [scanDebugLine, setScanDebugLine] = useState("");

  const logScan = (msg, extra = {}) => {
    if (!DEBUG_SCAN) return;
    const line = `[scan] ${msg} ${Object.keys(extra).length ? JSON.stringify(extra) : ""}`.trim();
    scanEventsRef.current.push({ t: Date.now(), line, extra });
    if (scanEventsRef.current.length > 80) scanEventsRef.current.shift();
    setScanDebugLine(line);
    // eslint-disable-next-line no-console
    console.log(line);
  };

  void scanDebugLine;

  const [totalKnown, setTotalKnown] = useState(null);

  const [err, setErr] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);

  // ✅ default random on load
  const [sortMode, setSortMode] = useState("random"); // "asc" | "desc" | "random"
  const [randomSeed, setRandomSeed] = useState(() => ((Date.now() >>> 0) || 1));

  const [page, setPage] = useState(1);

  // ✅ notify parent on page change (e.g., scroll to top)
  useEffect(() => {
    if (typeof onPageChange === "function") onPageChange(page);
  }, [page, onPageChange]);

  const wrapRef = useRef(null);
  const [cols, setCols] = useState(1);
  const [tile, setTile] = useState(140);

  const effectiveSortMode = launchGalleryMode ? "desc" : sortMode;

  const isDoneItem = (it) =>
    it &&
    (it.status === "done" || it.status === "completed") &&
    it.asset_id &&
    it.reveal_txid;

  // ✅ snapshot of page 1 so it renders first and stays stable while background scan runs
  const [page1Snapshot, setPage1Snapshot] = useState([]);
  const [page1Ready, setPage1Ready] = useState(false);

  const flatLoaded = useMemo(() => {
    if (previewMode) return previewItems;
    const out = [];
    const keys = Array.from(pageMap.keys()).sort((a, b) => a - b);
    for (const k of keys) {
      const items = pageMap.get(k) || [];
      out.push(...items);
    }
    return out;
  }, [previewMode, previewItems, pageMap]);

  const orderedLoaded = useMemo(
    () => sortGalleryItems(flatLoaded, previewMode, effectiveSortMode, randomSeed),
    [flatLoaded, previewMode, effectiveSortMode, randomSeed]
  );

  const orderedPage1 = useMemo(() => {
    return sortGalleryItems(page1Snapshot || [], previewMode, effectiveSortMode, randomSeed);
  }, [page1Snapshot, previewMode, effectiveSortMode, randomSeed]);

  // ✅ lock paging arrows until we’ve loaded the full fixed set (normal mode)
  const paginationReady = !launchGalleryMode && (!usingFixedTotals || loadedCount >= scanCap);

  // ✅ while loading: render ONLY the page-1 snapshot (prevents flashing/re-shuffles)
  const freezeToPage1 = usingFixedTotals && !paginationReady && page1Ready;

  // force page=1 while frozen
  useEffect(() => {
    if (freezeToPage1 && page !== 1) setPage(1);
  }, [freezeToPage1, page]);

  // list used for rendering + lightbox nav
  const activeOrderedList = useMemo(() => {
    if (launchGalleryMode) return orderedLoaded.slice(0, LAUNCH_GALLERY_MAX);
    if (freezeToPage1) return orderedPage1;
    return orderedLoaded;
  }, [launchGalleryMode, orderedLoaded, freezeToPage1, orderedPage1]);

  const rows = useMemo(() => {
    if (launchGalleryMode) return activeOrderedList.slice(0, LAUNCH_GALLERY_MAX);
    const start = (page - 1) * GALLERY_PAGE_SIZE;
    const end = start + GALLERY_PAGE_SIZE;
    return activeOrderedList.slice(start, end);
  }, [activeOrderedList, page, launchGalleryMode]);

  // Page-1-first load
  useEffect(() => {
    let alive = true;

    const resetAll = () => {
      setErr(null);
      setPreviewItems([]);

      const empty = new Map();
      setPageMap(empty);
      pageMapRef.current = empty;

      setLoadedCount(0);
      setScanCursor(null);
      setScanHasMore(false);
      setScanRunning(false);
      setTotalKnown(null);
      setPage(1);

      setPage1Snapshot([]);
      setPage1Ready(false);

      if (DEBUG_SCAN) logScan("resetAll");
    };

    const load = async () => {
      try {
        resetAll();

        if (previewMode) {
          const bucketParam = "&bucket=alt";
          const s3resp = await fetchJsonAllowError(`${ORCH_API}/list-s3?max=5000${bucketParam}`);
          if (!alive) return;

          if (!s3resp.ok) {
            setErr(s3resp.data?.error || `S3 list failed (${s3resp.status})`);
            if (DEBUG_SCAN) logScan("preview list-s3 failed", { status: s3resp.status });
            return;
          }

          let keys = Array.isArray(s3resp.data) ? s3resp.data : s3resp.data.keys || s3resp.data.items || [];

          const IMAGE_EXT = /\.(svg|png|jpe?g|webp)$/i;
          keys = (keys || []).filter((k) => k && !k.endsWith("/") && IMAGE_EXT.test(k));

          const items = keys.map((k, idx) => ({
            asset_id: k,
            status: "pending",
            preview_index: idx + 1,
          }));

          setPreviewItems(items);
          setTotalKnown(items.length);

          setPage1Snapshot(items.slice(0, GALLERY_PAGE_SIZE));
          setPage1Ready(true);

          if (DEBUG_SCAN) logScan("preview loaded", { count: items.length });
          return;
        }

        // ✅ Load page 1 first
        const firstRes = await fetchJsonAllowError(`${ORCH_API}/history?wrap=1&limit=${GALLERY_PAGE_SIZE}`);
        if (!alive) return;

        if (!firstRes.ok) {
          setErr(firstRes.data?.error || `History failed (${firstRes.status})`);
          if (DEBUG_SCAN) logScan("history page1 failed", { status: firstRes.status });
          return;
        }

        const first = firstRes.data || {};
        const done = (first.items || []).filter(isDoneItem);
        const page1 = done.slice(0, GALLERY_PAGE_SIZE);

        const m = new Map();
        m.set(1, page1);

        setPageMap(m);
        pageMapRef.current = m;

        // ✅ snapshot first page so it doesn't flash while scan streams in
        setPage1Snapshot(page1);
        setPage1Ready(true);

        setLoadedCount(page1.length);

        const next = first.next_cursor || null;
        setScanCursor(next);
        setScanHasMore(!!next && page1.length < scanCap);

        if (DEBUG_SCAN) {
          logScan("page1 loaded", {
            got: (first.items || []).length,
            done: done.length,
            page1: page1.length,
            nextCursor: !!next,
          });
        }
      } catch (e) {
        if (!alive) return;
        setErr(e.message || String(e));
        if (DEBUG_SCAN) logScan("page1 exception", { msg: e.message || String(e) });
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [previewMode, ORCH_API, scanCap]);

  // Background scan: fill future pages (runs only after page 1 is ready)
  useEffect(() => {
    if (previewMode) return;
    if (launchGalleryMode) return;
    if (!scanHasMore) return;
    if (!scanCursor) return;
    if (!page1Ready) return;

    if (scanLockRef.current) return;

    let alive = true;

    const run = async () => {
      scanLockRef.current = true;
      setScanRunning(true);

      if (DEBUG_SCAN) logScan("scan start", { hasCursor: !!scanCursor });

      try {
        const snapshotLoaded = (() => {
          let n = 0;
          for (const v of pageMapRef.current.values()) n += (v || []).length;
          return n;
        })();

        let cur = scanCursor;
        let loadedSoFar = snapshotLoaded;

        let nextPageNo = (() => {
          const keys = Array.from(pageMapRef.current.keys());
          const maxPage = keys.length ? Math.max(...keys) : 1;
          return maxPage + 1;
        })();

        let buffer = [];
        const seenCursors = new Set();

        while (alive && cur && loadedSoFar < scanCap) {
          if (seenCursors.has(cur)) {
            if (DEBUG_SCAN) logScan("cursor repeated; stopping", { curPreview: String(cur).slice(0, 80) });
            break;
          }
          seenCursors.add(cur);

          let cursorParam = cur;
          try {
            if (typeof cursorParam !== "string") cursorParam = JSON.stringify(cursorParam);
          } catch {
            cursorParam = String(cur || "");
          }
          if (!cursorParam) {
            if (DEBUG_SCAN) logScan("missing cursorParam; stopping");
            break;
          }

          const url =
            `${ORCH_API}/history?wrap=1&limit=${HISTORY_SCAN_LIMIT}&cursor=` + encodeURIComponent(cursorParam);

          const res = await fetchJsonAllowError(url);
          if (!res.ok) {
            if (DEBUG_SCAN) logScan("history fetch failed", { status: res.status });
            setScanHasMore(false);
            break;
          }

          const resp = res.data || {};
          const items = (resp.items || []).filter(isDoneItem);
          cur = resp.next_cursor || null;

          if (!items.length) {
            if (!cur) break;
            continue;
          }

          buffer.push(...items);

          while (buffer.length >= GALLERY_PAGE_SIZE && loadedSoFar < scanCap) {
            const chunk = buffer.slice(0, GALLERY_PAGE_SIZE);
            buffer = buffer.slice(GALLERY_PAGE_SIZE);

            const remaining = scanCap - loadedSoFar;
            const take = remaining >= chunk.length ? chunk : chunk.slice(0, remaining);
            if (!take.length) break;

            const pageNo = nextPageNo;

            setPageMap((prev) => {
              const nm = new Map(prev);
              nm.set(pageNo, take);
              return nm;
            });

            loadedSoFar += take.length;
            nextPageNo += 1;
          }
        }

        // flush remainder
        if (alive && buffer.length && loadedSoFar < scanCap) {
          const remaining = scanCap - loadedSoFar;
          const take = remaining >= buffer.length ? buffer : buffer.slice(0, remaining);

          if (take.length) {
            const pageNo = nextPageNo;

            setPageMap((prev) => {
              const nm = new Map(prev);
              nm.set(pageNo, take);
              return nm;
            });

            loadedSoFar += take.length;
          }
        }

        if (!alive) return;

        setTotalKnown(loadedSoFar);
        setScanCursor(cur || null);
        setScanHasMore(!!cur && loadedSoFar < scanCap);

        // ✅ update loadedCount only once at end (keeps UI frozen until fully loaded)
        setLoadedCount(loadedSoFar);

        if (DEBUG_SCAN) {
          logScan("scan end", {
            loadedSoFar,
            hasCursor: !!cur,
            hasMore: !!cur && loadedSoFar < scanCap,
          });
        }
      } catch (e) {
        if (!alive) return;
        setErr(e.message || String(e));
        setScanHasMore(false);
        if (DEBUG_SCAN) logScan("scan exception", { msg: e.message || String(e) });
      } finally {
        scanLockRef.current = false;
        setScanRunning(false);
        if (DEBUG_SCAN) logScan("scan finally", { lock: 0, running: 0 });
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [previewMode, launchGalleryMode, scanHasMore, scanCursor, ORCH_API, page1Ready, scanCap]);

  // reset paging on sort changes / mode toggles
  useEffect(() => {
    setPage(1);
  }, [sortMode, randomSeed, previewMode, launchGalleryMode]);

  // layout compute
  useEffect(() => {
    const compute = () => {
      if (!wrapRef.current) return;
      const w = wrapRef.current.clientWidth || 0;
      if (w <= 0) return;

      if (w < MOBILE_BREAKPOINT) {
        setCols(1);
        setTile(w);
        return;
      }

      if (sizeMode === "xl") {
        const maxXL = 700;
        const tw = Math.min(w, maxXL);
        setCols(1);
        setTile(tw);
        return;
      }

      const conf = SIZE_PRESETS[sizeMode] || SIZE_PRESETS.small;
      const { min, target, max } = conf;

      let c = Math.max(1, Math.round(w / (target + TILE_GAP)));
      let tw = Math.floor((w - TILE_GAP * (c - 1)) / c);

      if (tw < min) {
        c = Math.max(1, Math.floor((w + TILE_GAP) / (min + TILE_GAP)));
        tw = Math.floor((w - TILE_GAP * (c - 1)) / c);
      } else if (tw > max) {
        const cMax = Math.max(1, Math.floor((w + TILE_GAP) / (min + TILE_GAP)));
        while (tw > max && c < cMax) {
          c += 1;
          tw = Math.floor((w - TILE_GAP * (c - 1)) / c);
        }
        tw = Math.min(Math.max(tw, min), max);
      }

      setCols(c);
      setTile(tw);
    };

    compute();
    const ro = new ResizeObserver(compute);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [sizeMode]);

  // ✅ UI totals (stable in normal mode)
  const uiTotalItemsDisplay = usingFixedTotals ? String(FIXED_TOTAL_ITEMS) : totalKnown == null ? "…" : String(totalKnown);
  const uiTotalPages = usingFixedTotals ? FIXED_TOTAL_PAGES : Math.max(1, Math.ceil((totalKnown ?? 1) / GALLERY_PAGE_SIZE));

  // ✅ arrows unlocked only after loading completes
  const canPrev = !launchGalleryMode && paginationReady && page > 1;
  const canNext = !launchGalleryMode && paginationReady && page < uiTotalPages;

  const gotoPrev = () => {
    if (canPrev) setPage((p) => p - 1);
  };

  const gotoNext = () => {
    if (canNext) setPage((p) => p + 1);
  };

  const displayStartIndex = (() => {
    if (launchGalleryMode) return 1;
    return (page - 1) * GALLERY_PAGE_SIZE + 1;
  })();

  const displayEndIndex = (() => {
    if (launchGalleryMode) return rows.length;
    if (usingFixedTotals) return Math.min(page * GALLERY_PAGE_SIZE, FIXED_TOTAL_ITEMS);

    const knownTotal = totalKnown ?? rows.length ?? 0;
    const fallbackEnd = page * GALLERY_PAGE_SIZE;
    const clampedTotal = knownTotal || fallbackEnd;
    return Math.min(fallbackEnd, clampedTotal);
  })();

  const gridDynamic = {
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, ${tile}px)`,
    justifyContent: sizeMode === "xl" ? "center" : "start",
    columnGap: cols === 1 ? 0 : TILE_GAP,
    rowGap: sizeMode === "small" ? 4 : TILE_GAP,
  };

  // smoothing behavior
  let gridPixelated;
  let lightboxPixelated;
  if (smoothingMode === "smooth") {
    gridPixelated = false;
    lightboxPixelated = false;
  } else if (smoothingMode === "pixelated") {
    gridPixelated = true;
    lightboxPixelated = true;
  } else {
    gridPixelated = sizeMode === "xl";
    lightboxPixelated = true;
  }
  if (cols === 1) {
    gridPixelated = true;
    lightboxPixelated = true;
  }

  const [lightboxItem, setLightboxItem] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [lightboxHover, setLightboxHover] = useState(false);

  const buildLightboxPayload = (it, orderedIndex) => {
    if (!it) return null;

    const asset = it.asset_id;
    const isPending = it.status === "pending";

    let displayName;
    let idText;
    let knotNumber = null;
    let inscriptionUrl = null;

    if (previewMode) {
      const globalIndex = it.preview_index || orderedIndex + 1;
      displayName = `Knot ${globalIndex}`;
      idText = PENDING_ID;
      knotNumber = globalIndex;
    } else {
      const txBase = cleanTxid(it.reveal_txid || "");
      idText = txBase ? formatInscriptionId(txBase) : PENDING_ID;
      inscriptionUrl = txBase ? ordLink(txBase) : null;

      const n = getKnotNoNumeric(it);
      displayName = Number.isFinite(n) ? `Knot ${n}` : it?.knot_name || displayKnotName(it);
      knotNumber = Number.isFinite(n) ? n : null;
    }

    const imgSrc = previewMode
      ? `${ORCH_API}/file-any/${encodeURIComponent(asset)}?bucket=alt`
      : isPending
      ? `${ORCH_API}/file-any/${encodeURIComponent(asset)}`
      : `${ORCH_API}/file-public-url/${encodeURIComponent(asset)}`;

    const downloadUrl = previewMode
      ? `${ORCH_API}/file-any/${encodeURIComponent(asset)}?bucket=alt`
      : isPending
      ? `${ORCH_API}/file-any/${encodeURIComponent(asset)}`
      : `${ORCH_API}/file-public/${encodeURIComponent(asset)}`;

    return {
      displayName,
      imgSrc,
      downloadUrl,
      inscriptionId: idText,
      inscriptionUrl,
      knotNumber,
      asset,
    };
  };

  const openLightboxByIndex = (idx) => {
    if (!Number.isFinite(idx)) return;

    const listForNav = activeOrderedList;
    if (!listForNav.length) return;

    const safeIdx = Math.max(0, Math.min(listForNav.length - 1, idx));
    const it = listForNav[safeIdx];
    const payload = buildLightboxPayload(it, safeIdx);
    setLightboxIndex(safeIdx);
    setLightboxItem(payload);
  };

  const closeLightbox = () => {
    setLightboxItem(null);
    setLightboxIndex(null);
  };

  // keyboard: Esc closes, arrows navigate
  useEffect(() => {
    const onKey = (e) => {
      if (!lightboxItem) return;

      if (e.key === "Escape") {
        e.preventDefault();
        closeLightbox();
        return;
      }

      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        const listForNav = activeOrderedList;
        if (!listForNav.length) return;

        const cur = Number.isFinite(lightboxIndex) ? lightboxIndex : 0;
        const dir = e.key === "ArrowRight" ? 1 : -1;
        const next = (cur + dir + listForNav.length) % listForNav.length;

        openLightboxByIndex(next);
      }
    };

    if (lightboxItem) {
      document.addEventListener("keydown", onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
        document.removeEventListener("keydown", onKey);
      };
    }
  }, [lightboxItem, lightboxIndex, activeOrderedList]);

  const lightboxTextColor = styles?.lightboxTextColor || "#FFFFFF";
  const tileIconColor =
    (styles?.metaText && styles.metaText.color) || (styles?.page && styles.page.color) || "#949494";

  const HeaderControls = () => (
    <div style={styles.galleryControlsBar}>
      <div style={{ display: "flex", alignItems: "center", gap: 26, flexWrap: "wrap" }}>
        {/* sort cluster + ME link */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {launchGalleryMode ? (
            <button style={styles.sizeLinkActive}>latest</button>
          ) : (
            <>
              <button
                onClick={() => setSortMode("asc")}
                style={sortMode === "asc" ? styles.sizeLinkActive : styles.sizeLink}
              >
                asc
              </button>
              <span style={{ fontSize: 14, opacity: 0.75 }}>/</span>
              <button
                onClick={() => setSortMode("desc")}
                style={sortMode === "desc" ? styles.sizeLinkActive : styles.sizeLink}
              >
                desc
              </button>
              <span style={{ fontSize: 14, opacity: 0.75 }}>/</span>
              <button
                onClick={() => {
                  if (sortMode !== "random") setSortMode("random");
                  setRandomSeed((s) => s + 1);
                }}
                style={sortMode === "random" ? styles.sizeLinkActive : styles.sizeLink}
                title={sortMode === "random" ? "Re-roll random order" : "Random order"}
              >
                random
              </button>

              {/* ✅ ME link */}
              <span style={{ fontSize: 14, opacity: 0.75 }}>/</span>
              <a
                href={MAGIC_EDEN_URL}
                target="_blank"
                rel="noreferrer"
                style={{
                  ...styles.sizeLink,
                  display: "inline-block",
                }}
                title="Magic Eden"
              >
                ME
              </a>
            </>
          )}
        </div>
      </div>

      {/* right: counter + paging */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 700, opacity: 0.9 }}>
            {displayStartIndex}–{displayEndIndex} of {uiTotalItemsDisplay}
          </span>

          {!launchGalleryMode && (
            <>
              <button
                onClick={gotoPrev}
                disabled={!canPrev}
                aria-label="Previous page"
                style={{
                  ...(canPrev ? styles.btnSquare : styles.btnSquareDisabled),
                  marginLeft: 4,
                }}
              >
                ‹
              </button>
              <span style={{ fontSize: 14, fontWeight: 700, opacity: 0.9, textAlign: "center", minWidth: 36 }}>
                {Math.min(page, uiTotalPages)} / {uiTotalPages}
              </span>
              <button
                onClick={gotoNext}
                disabled={!canNext}
                aria-label="Next page"
                style={canNext ? styles.btnSquare : styles.btnSquareDisabled}
              >
                ›
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {err && <p style={styles.errLight}>{err}</p>}

      <div style={{ paddingRight: 16, boxSizing: "border-box" }}>
        <HeaderControls />

        <div ref={wrapRef}>
          <div style={gridDynamic}>
            {rows.map((it, idx) => {
              const asset = it.asset_id;
              const isPending = it.status === "pending";

              const globalOrderedIndex = launchGalleryMode ? idx : (page - 1) * GALLERY_PAGE_SIZE + idx;

              let displayName;
              let idText;
              let link = null;
              let knotNumber = null;

              if (previewMode) {
                const globalIndex = it.preview_index || globalOrderedIndex + 1;
                displayName = `Knot ${globalIndex}`;
                idText = PENDING_ID;
                knotNumber = globalIndex;
              } else {
                const txBase = cleanTxid(it.reveal_txid || "");
                link = txBase ? ordLink(txBase) : null;
                idText = txBase ? formatInscriptionId(txBase) : PENDING_ID;

                const n = getKnotNoNumeric(it);
                displayName = Number.isFinite(n) ? `Knot ${n}` : it?.knot_name || displayKnotName(it);
                knotNumber = Number.isFinite(n) ? n : null;
              }

              const imgSrc = previewMode
                ? `${ORCH_API}/file-any/${encodeURIComponent(asset)}?bucket=alt`
                : isPending
                ? `${ORCH_API}/file-any/${encodeURIComponent(asset)}`
                : `${ORCH_API}/file-public-url/${encodeURIComponent(asset)}`;

              const downloadUrl = previewMode
                ? `${ORCH_API}/file-any/${encodeURIComponent(asset)}?bucket=alt`
                : isPending
                ? `${ORCH_API}/file-any/${encodeURIComponent(asset)}`
                : `${ORCH_API}/file-public/${encodeURIComponent(asset)}`;

              const key = `${asset}-${previewMode ? "preview" : cleanTxid(it.reveal_txid || "") || "pending"}`;

              const onDownload = async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await downloadPngOriginal(downloadUrl, knotNumber);
              };

              const showTileHover = hoverKey === key;

              return (
                <div
                  key={key}
                  style={{
                    background: "transparent",
                    border: "none",
                    borderRadius: 0,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 200,
                    position: "relative",
                  }}
                  onMouseEnter={() => setHoverKey(key)}
                  onMouseLeave={() => setHoverKey(null)}
                >
                  <LazyImage
                    src={imgSrc}
                    alt={displayName}
                    tileBoxStyle={styles.tileImgBox}
                    placeholderStyle={styles.imgPlaceholder}
                    pixelate={gridPixelated}
                    onClick={() => openLightboxByIndex(globalOrderedIndex)}
                  />

                  <div style={{ marginTop: sizeMode === "small" ? 4 : 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap" }}>
                      <span style={{ ...styles.metaText, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                        {displayName}
                      </span>

                      {showTileHover && (
                        <button
                          aria-label="Download PNG"
                          title="Download PNG"
                          onClick={onDownload}
                          onMouseDown={(e) => e.preventDefault()}
                          tabIndex={-1}
                          style={{
                            background: "transparent",
                            border: "none",
                            padding: 0,
                            margin: 0,
                            outline: "none",
                            boxShadow: "none",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <MonoIcon src={DL_ICON_SRC} size={10} color={tileIconColor} opacity={1} />
                        </button>
                      )}
                    </div>

                    <div style={{ marginTop: 0 }}>
                      {link && !previewMode ? (
                        <a
                          href={link}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            ...styles.metaText,
                            fontSize: 12,
                            fontWeight: 700,
                            textDecoration: "underline",
                            outline: "none",
                            whiteSpace: "nowrap",
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          onFocus={(e) => e.target.blur()}
                          title={idText}
                        >
                          {idText}
                        </a>
                      ) : (
                        <span
                          style={{ ...styles.metaText, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
                          title={idText}
                        >
                          {idText}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {lightboxItem && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closeLightbox}
          style={styles.lightboxBackdrop("#000000")}
          onMouseEnter={() => setLightboxHover(true)}
          onMouseLeave={() => setLightboxHover(false)}
        >
          <button
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
            style={{
              ...styles.lightboxClose,
              opacity: lightboxHover ? 1 : 0.8,
            }}
          >
            ×
          </button>

          <div style={styles.lightboxCenter} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                ...styles.lightboxInlineWrap,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  ...styles.lightboxImageWrap,
                  width: "min(92vw, 1400px)",
                  height: "min(82vh, 900px)",
                  flex: "0 0 auto",
                }}
              >
                <img
                  src={lightboxItem.imgSrc}
                  alt={lightboxItem.displayName}
                  draggable={false}
                  style={{
                    ...styles.lightboxImg(lightboxPixelated),
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>

              <div
                style={{
                  ...styles.lightboxCaption,
                  opacity: lightboxHover ? 1 : 0.8,
                  width: "min(92vw, 1400px)",
                  marginTop: 8,
                  textAlign: "left",
                  alignSelf: "flex-start",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", color: lightboxTextColor }}>
                    {lightboxItem.displayName}
                  </span>

                  <button
                    aria-label="Download PNG"
                    title="Download PNG"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await downloadPngOriginal(lightboxItem.downloadUrl, lightboxItem.knotNumber ?? null);
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      margin: 0,
                      outline: "none",
                      boxShadow: "none",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    <MonoIcon src={DL_ICON_SRC} size={10} color={lightboxTextColor} opacity={1} />
                  </button>
                </div>

                {lightboxItem.inscriptionId && (
                  <div
                    style={{
                      marginTop: 2,
                      fontSize: 12,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      color: lightboxTextColor,
                    }}
                    title={lightboxItem.inscriptionId}
                  >
                    {lightboxItem.inscriptionUrl ? (
                      <a
                        href={lightboxItem.inscriptionUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: "inherit",
                          textDecoration: "underline",
                          outline: "none",
                          whiteSpace: "nowrap",
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        onFocus={(e) => e.target.blur()}
                      >
                        {lightboxItem.inscriptionId}
                      </a>
                    ) : (
                      lightboxItem.inscriptionId
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* keep around for quick inspection if you flip DEBUG_SCAN=true */}
      {DEBUG_SCAN && scanRunning && (
        <div style={{ paddingRight: 16, paddingTop: 8, fontSize: 11, opacity: 0.6 }}>
          scanning… loadedCount={loadedCount} cursor={scanCursor ? "yes" : "no"}
        </div>
      )}
    </>
  );
}
